import { usePlcApiCall } from '../api.hook';
import { BackgroundColorForecast, buildTableData } from './Treatment.helper';
import { Geopoint } from 'pleinchamp-api-client';
import { useEffect, useMemo, useState } from 'react';
import { callTreatmentScheduleForecasts } from '@meteo/business/Meteo.api';
import { plcDayjs } from '@utils/date';
import { Dayjs } from 'dayjs';
import { AxiosError } from 'pleinchamp-api-client/node_modules/axios';

export type Range = { min: number; max: number };

export type ReasonsValue = 'W' | 'R' | 'T' | 'H' | 'WR';
export type AllReasonsValue = ReasonsValue | 'M' | '';

export type FinalResponse = {
  date: Dayjs;
  score: number;
  reason: AllReasonsValue;
  color: BackgroundColorForecast;
};

export type TreatmentData = {
  date: Dayjs;
  data: FinalResponse[];
};

export interface ForecastQueries {
  humiditySecurityBound: number;
  precipitationAmountRange: Range;
  precipitationProbabilityRange: Range;
  relativeHumidityRange: Range;
  temperatureRange: Range;
  temperatureSecurityBounds: { in: number; out: number };
  windSpeedRange: Range;
}

export enum TableDataItemKeyEnum {
  'hour-0',
  'hour-1',
  'hour-2',
  'hour-3',
  'hour-4',
  'hour-5',
  'hour-6',
  'hour-7',
  'hour-8',
  'hour-9',
  'hour-10',
  'hour-11',
  'hour-12',
  'hour-13',
  'hour-14',
  'hour-15',
  'hour-16',
  'hour-17',
  'hour-18',
  'hour-19',
  'hour-20',
  'hour-21',
  'hour-22',
  'hour-23',
}
export type TableDataItem = {
  label: string;
  date: Dayjs;
} & {
  [key in TableDataItemKeyEnum]: FinalResponse;
};

function fetchTreatmentScheduleForecasts(coordinates?: Geopoint) {
  if (!coordinates) {
    return Promise.resolve(undefined);
  }
  const { latitude, longitude } = coordinates;
  return callTreatmentScheduleForecasts(latitude, longitude);
}

const forecastQueriesInitState = {
  humiditySecurityBound: 5,
  precipitationAmountRange: {
    max: 1.25,
    min: 0,
  },
  precipitationProbabilityRange: {
    max: 60,
    min: 20,
  },
  relativeHumidityRange: {
    max: 85,
    min: 55,
  },
  temperatureRange: { max: 25, min: 10 },
  temperatureSecurityBounds: { in: 2, out: 2 },
  windSpeedRange: { max: 19, min: 0 },
};

function useForecastTool({ coordinates }: { coordinates?: Geopoint }) {
  const from = useMemo(() => plcDayjs().startOf('day'), []);
  // const to = useMemo(() => from.add(7, 'day'), [from]);
  const {
    data: prevision,
    isValidating: isLoading,
    error: previsionErrorNotCasted,
    retryCallback: getPrevision,
  } = usePlcApiCall(fetchTreatmentScheduleForecasts, [coordinates]);
  // Need to cast the error object  to workaround a typing issue
  const previsionError = previsionErrorNotCasted as AxiosError | undefined;

  const [forecastQueries, setForecastQueries] = useState<ForecastQueries>(forecastQueriesInitState);

  const [tableData, setTableData] = useState<TableDataItem[]>([]);

  useEffect(() => {
    if (prevision) {
      setTableData(buildTableData({ forecastQueries, from, treatmentScheduleForecasts: prevision }));
    }
  }, [prevision]);

  return {
    forecastQueries,
    getPrevision,
    isLoading,
    prevision,
    previsionError,
    setForecastQueries,
    tableData,
  };
}

export default useForecastTool;
